import { CODE_MAX_LENGTH } from '../common/constants';
import { Entity } from '../common/queryClient';
import { CategoryCode } from './schema/CategoryCode';
import { z } from 'zod/v3';
import { zIsoDate, zName } from '../common/utils';
import { CategorySelectSchema } from './schema/CategorySearchResultSchema';

export interface ClassifierFilterDto {
  code?: string;
  name?: string;
  description?: string;
  hasCategories?: boolean;
  inUse?: boolean | '';
  search?: string;
  validFrom?: Date | null;
  validTo?: Date | null;
}

export interface ClassifiersDataSource
  extends Omit<
    ClassifierOverviewDto,
    'validFrom' | 'validTo' | 'hasCategories'
  > {
  validPeriod: string | null;
}

// Response object interfaces
export interface CategoryRelationDto {
  id: number | null;
  subCategoryCode: string;
  subCategoryName?: string;
  subCategoryType: string;
  sequenceNumber: number;
  validFrom: string;
  validTo: string | null;
}

export interface CategoryBasicDto {
  id: number | null;
  code: CategoryCode | string;
  name: string;
  nameEng: string | null;
  description: string | null;
  categoryType: string | null;
  sequenceNumber: number;
  childSequenceNumber: number;
  levelNumber: number;
  parentCode: string | null;
  validFrom: string;
  validTo: string | null;
}

export interface CategoryDto extends CategoryBasicDto {
  inUse: boolean;
  categoryRelations: CategoryRelationDto[] | null;
}

export interface CategoryReadDto extends CategoryDto {
  id: number;
}

export interface ClassifierOverviewDto extends Entity {
  code: string;
  name: string;
  description: string | null;
  categoryCount: number | null;
  groupCount: number | null;
  validFrom: string;
  validTo: string | null;
  inUse: boolean;
  hasCategories: boolean;
}

// Form interfaces
export const CategoryGroupSchema = z.object({
  id: z.number(),
  code: z.string().min(1).max(CODE_MAX_LENGTH),
  name: zName(),
  categoryType: z.string(),
  validFrom: zIsoDate().nullable(),
  validTo: zIsoDate().nullable(),
});
export type CategoryGroupType = z.infer<typeof CategoryGroupSchema>;

export const ClassifierGroupSchema = z.object({
  id: z.number(),
  code: z.string().min(1).max(CODE_MAX_LENGTH),
  name: zName(),
  validFrom: zIsoDate().nullable(),
  validTo: zIsoDate().nullable(),
  categories: z.array(CategoryGroupSchema).nullable(),
  allCategories: z.boolean().nullish(),
  categoryHierarchyLevelNumbers: z.number().array().nullish(),
  hasCategories: z.boolean(),
});
export type ClassifierGroupType = z.infer<typeof ClassifierGroupSchema>;

export const ClassifierGroupArraySchema = z.array(ClassifierGroupSchema);
export type ClassifierGroupArrayType = z.infer<
  typeof ClassifierGroupArraySchema
>;

export type ClassifierSortField = keyof Omit<
  ClassifierOverviewDto,
  'id' | 'modifiedBy' | 'modifiedAt'
>;

export type CategorySelectType = z.infer<typeof CategorySelectSchema>;
